import { EDLINK_FIREBASE_PROVIDER_ID, readEdlinkInitiationParams, stripEdlinkInitiationParams } from '@airreading/auth';
import {
  User as FirebaseUser,
  GoogleAuthProvider,
  OAuthProvider,
  browserLocalPersistence,
  getRedirectResult,
  onIdTokenChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getEnvironmentLevel } from '@/lib/common-utils.js';
import { logError } from '@/lib/error-logger.js';
import {
  authFailureFromProviderError,
  type AuthFailure,
  type SignInFlow,
} from './auth-errors.js';
import { auth } from './firebase.js';

const googleOauthProvider = new GoogleAuthProvider();
googleOauthProvider.setCustomParameters({
  prompt: 'select_account',
});

const redirectInProgressKey = 'auth:redirectInProgress';
const signInFlowKey = 'auth:signInFlow';

export type ProviderSignInResult = 'success' | 'redirecting' | 'cancelled' | 'failed';

function storedSignInFlow(): SignInFlow | undefined {
  const flow = sessionStorage.getItem(signInFlowKey);
  return flow === 'google' || flow === 'school' ? flow : undefined;
}

function signInFlowForUser(user: FirebaseUser): SignInFlow {
  return user.providerData.some(({ providerId }) => providerId === EDLINK_FIREBASE_PROVIDER_ID)
    ? 'school'
    : storedSignInFlow() ?? 'google';
}

export function useFirebaseAuth() {
  const [identityProviderUser, setIdentityProviderUser] = useState<FirebaseUser | null | undefined>(undefined);
  const [identityProviderFlow, setIdentityProviderFlow] = useState<SignInFlow | undefined>();
  const [providerFailure, setProviderFailure] = useState<AuthFailure | undefined>();

  // localhost uses popup since redirect auth is not reliable without
  // a same-site authDomain (/__/auth/* proxy)
  const shouldUseRedirect = getEnvironmentLevel() !== 'local';
  const edlinkInitiationStarted = useRef(false);

  const handleProviderFailure = useCallback((error: unknown, flow: SignInFlow): ProviderSignInResult => {
    const failure = authFailureFromProviderError(flow, error);
    if (!failure) {
      return 'cancelled';
    }

    setProviderFailure(failure);
    logError(error instanceof Error ? error : new Error(`${flow} identity-provider sign-in failed`, { cause: error }), {
      component: 'useFirebaseAuth',
      action: flow === 'school' ? 'signInWithEdlink' : 'signInWithGoogle',
      providerFlow: flow,
    });
    return 'failed';
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      if (user) {
        setIdentityProviderFlow(signInFlowForUser(user));
      }
      setIdentityProviderUser(user ?? null);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(redirectInProgressKey) !== '1') {
      return;
    }

    let active = true;
    const flow = storedSignInFlow() ?? 'google';

    void getRedirectResult(auth)
      .catch((error: unknown) => {
        if (active) {
          handleProviderFailure(error, flow);
        }
      })
      .finally(() => {
        sessionStorage.removeItem(redirectInProgressKey);
        sessionStorage.removeItem(signInFlowKey);
      });

    return () => {
      active = false;
    };
  }, [handleProviderFailure]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (sessionStorage.getItem(redirectInProgressKey) === '1') {
      return;
    }

    const edlinkClientId = import.meta.env.VITE_EDLINK_CLIENT_ID;

    if (!edlinkClientId) {
      logError(new Error('VITE_EDLINK_CLIENT_ID is not configured'), {
        component: 'useFirebaseAuth',
        action: 'signInWithEdlink',
      });
      return;
    }

    const edlinkInitiationParams = readEdlinkInitiationParams(new URL(window.location.href), edlinkClientId);

    if (!edlinkInitiationParams || edlinkInitiationStarted.current || Boolean(identityProviderUser)) {
      return;
    }

    edlinkInitiationStarted.current = true;

    const cleanUrl = new URL(stripEdlinkInitiationParams(window.location.href));
    // Clear any stale redirect from a previous user's session so the incoming
    // student lands on their own home page after SSO completes.
    cleanUrl.searchParams.delete('redirect');
    history.replaceState(null, '', cleanUrl.toString());

    const provider = new OAuthProvider(EDLINK_FIREBASE_PROVIDER_ID);
    provider.setCustomParameters({ login_hint: edlinkInitiationParams.loginHint });

    (async () => {
      try {
        await auth.setPersistence(browserLocalPersistence);
        const signInMethod = shouldUseRedirect ? signInWithRedirect : signInWithPopup;

        if (shouldUseRedirect) {
          sessionStorage.setItem(redirectInProgressKey, '1');
        }

        sessionStorage.setItem(signInFlowKey, 'school');

        await signInMethod(auth, provider);
      } catch (error: unknown) {
        edlinkInitiationStarted.current = false;
        sessionStorage.removeItem(redirectInProgressKey);
        sessionStorage.removeItem(signInFlowKey);
        handleProviderFailure(error, 'school');
      }
    })();
  }, [shouldUseRedirect, identityProviderUser, handleProviderFailure]);

  const signInWithGoogle = useCallback(async (): Promise<ProviderSignInResult> => {
    setProviderFailure(undefined);

    try {
      await auth.setPersistence(browserLocalPersistence);
      sessionStorage.setItem(signInFlowKey, 'google');

      if (shouldUseRedirect) {
        sessionStorage.setItem(redirectInProgressKey, '1');
      }

      const signInMethod = shouldUseRedirect ? signInWithRedirect : signInWithPopup;
      await signInMethod(auth, googleOauthProvider);
      return shouldUseRedirect ? 'redirecting' : 'success';
    } catch (error: unknown) {
      sessionStorage.removeItem(redirectInProgressKey);
      sessionStorage.removeItem(signInFlowKey);
      return handleProviderFailure(error, 'google');
    }
  }, [shouldUseRedirect, handleProviderFailure]);

  const logout = useCallback(async () => {
    sessionStorage.removeItem(redirectInProgressKey);
    sessionStorage.removeItem(signInFlowKey);
    setProviderFailure(undefined);
    return signOut(auth);
  }, []);

  const clearProviderFailure = useCallback(() => setProviderFailure(undefined), []);

  return {
    identityProviderUser,
    identityProviderFlow,
    providerFailure,
    clearProviderFailure,
    signInWithGoogle,
    logout,
  };
}
